import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "./AdminDetailDrawer.css";

// Right-hand panel for admin lists: the table stays compact and every detail
// and action for the selected row lives here. Closes on Esc or backdrop click.
// Rendered into <body> so admin layout containers can't clip or offset it.
export default function AdminDetailDrawer({ open, title, subtitle, badge, onClose, children, footer, testId }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="admin-drawer-backdrop" onMouseDown={onClose} data-testid={testId}>
      <aside
        className="admin-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Details"}
        tabIndex={-1}
        ref={panelRef}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="admin-drawer-head">
          <div className="admin-drawer-heading">
            <div className="admin-drawer-title">{title}</div>
            {subtitle && <div className="admin-drawer-subtitle">{subtitle}</div>}
            {badge && <div className="admin-drawer-badge">{badge}</div>}
          </div>
          <button type="button" className="admin-drawer-close" onClick={onClose} aria-label="Close details">
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        <div className="admin-drawer-body">{children}</div>
        {footer && <footer className="admin-drawer-footer">{footer}</footer>}
      </aside>
    </div>,
    document.body,
  );
}

export function DrawerSection({ title, children }) {
  return (
    <section className="admin-drawer-section">
      {title && <h3>{title}</h3>}
      {children}
    </section>
  );
}

// Label/value pairs; values that are empty render as an em dash.
export function DrawerFacts({ items }) {
  return (
    <dl className="admin-drawer-facts">
      {items.filter(Boolean).map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value === null || value === undefined || value === "" ? "—" : value}</dd>
        </div>
      ))}
    </dl>
  );
}
